import React, { useState, useEffect, useRef } from "react";
import { Star, Quote, Check, ChevronLeft, ChevronRight } from "lucide-react";
import Testi1Image from "../../assets/images/testimonials/testi1.png";
import Testi2Image from "../../assets/images/testimonials/testi2.png";
import Testi3Image from "../../assets/images/testimonials/testi3.png";

const testimonials = [
  {
    image: Testi1Image,
    quote:
      "BeyondLeadz's database is comprehensive and well-maintained, providing accurate contact information and detailed segmentation that allows me to tailor my outreach to specific audiences. The data is also verified and updated regularly, giving me confidence that I'm reaching the right people at the right time.",
    name: "Operations Manager",
    company: "Leading Hotel Chain in Delhi",
    rating: 5,
  },
  {
    image: Testi2Image,
    quote:
      "I highly recommend BeyondLeadz to anyone looking for high-quality data to enhance their marketing efforts. They are a reliable and trustworthy partner, and I look forward to continuing our collaboration in the future.",
    name: "Front Office Manager",
    company: "5-Star Hotel in Jaipur",
    rating: 5,
  },
  {
    image: Testi3Image,
    quote:
      "I have been using BeyondLeadz for some time now, and I must say that their data is top-notch. As a marketer, I rely heavily on accurate and up-to-date data to reach my target audience, and BeyondLeadz has consistently delivered.",
    name: "Marketing Manager",
    company: "Leading Beachfront Resort in Thailand",
    rating: 5,
  },
];

const StarRating = ({ count = 5 }) => (
  <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="tw-w-4 tw-h-4 tw-text-amber-400 tw-fill-amber-400" />
    ))}
  </div>
);

const QUOTE_ICON = (
  <Quote className="tw-w-10 tw-h-10 tw-text-[#5D44FF] tw-opacity-20 tw-flex-shrink-0" />
);

const Testimonials = () => {
  const [active, setActive] = useState(0);
  const [animDir, setAnimDir] = useState(""); // "left" | "right"
  const sectionRef = useRef(null);
  const autoRef = useRef(null);

  /* Auto-advance every 5 s */
  const startAuto = () => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => goNext(), 5000);
  };

  useEffect(() => {
    startAuto();
    return () => clearInterval(autoRef.current);
  }, [active]); // eslint-disable-line

  const goTo = (idx, dir) => {
    if (idx === active) return;
    setAnimDir(dir);
    setTimeout(() => {
      setActive(idx);
      setAnimDir("");
    }, 260);
  };

  const goPrev = () => {
    const prev = (active - 1 + testimonials.length) % testimonials.length;
    startAuto();
    goTo(prev, "right");
  };

  const goNext = () => {
    const next = (active + 1) % testimonials.length;
    goTo(next, "left");
  };

  const t = testimonials[active];

  return (
    <section
      className="tw-py-20 lg:tw-py-28 tw-bg-white tw-overflow-hidden"
      id="testimonials"
    >
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">

        {/* ── Section Header ── */}
        <div className="tw-text-center tw-mb-14">
          <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-font-bold tw-text-[#5D44FF] tw-uppercase tw-tracking-widest tw-mb-4 tw-bg-indigo-50 tw-px-4 tw-py-1.5 tw-rounded-full tw-border tw-border-solid tw-border-indigo-100">
            <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-[#5D44FF] tw-inline-block" />
            Client Stories
          </span>
          <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-extrabold tw-text-slate-900 tw-mb-3 tw-leading-tight">
            What Our <span className="tw-text-[#5D44FF]">Clients Say!</span>
          </h2>
          <p className="tw-text-base tw-text-slate-500">
            Valuable &amp; Commendable Feedback from Our Customers
          </p>
        </div>

        {/* ── Main Card ── */}
        <div>
          <div className="tw-relative tw-bg-white tw-rounded-3xl tw-border tw-border-solid tw-border-slate-100 tw-shadow-xl tw-shadow-slate-100/80 tw-overflow-hidden">



            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12">

              {/* Left: Client photo + info panel */}
              <div className="lg:tw-col-span-4 tw-bg-indigo-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-10 tw-text-center tw-relative">
                {/* Avatar ring */}
                <div className="tw-relative tw-mb-6">
                  <div className="tw-w-28 tw-h-28 tw-rounded-full tw-ring-4 tw-ring-white tw-ring-offset-2 tw-ring-offset-indigo-50 tw-overflow-hidden tw-shadow-xl">
                    <img
                      src={t.image}
                      alt={t.name}
                      className={`tw-w-full tw-h-full tw-object-cover tw-transition-all tw-duration-300 ${animDir ? "tw-opacity-0 tw-scale-95" : "tw-opacity-100 tw-scale-100"}`}
                    />
                  </div>
                  {/* Green verified badge */}
                  <div className="tw-absolute tw-bottom-1 tw-right-1 tw-w-7 tw-h-7 tw-rounded-full tw-bg-emerald-400 tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-solid tw-border-white tw-shadow">
                    <Check className="tw-w-3.5 tw-h-3.5 tw-text-white" />
                  </div>
                </div>

                {/* Name + company */}
                <div className={`tw-transition-all tw-duration-300 ${animDir ? "tw-opacity-0 tw-translate-y-2" : "tw-opacity-100 tw-translate-y-0"}`}>
                  <p className="tw-text-base tw-font-extrabold tw-text-slate-900 tw-mb-1">{t.name}</p>
                  <p className="tw-text-xs tw-text-slate-500 tw-font-medium tw-mb-4">{t.company}</p>
                  <StarRating count={t.rating} />
                </div>

                {/* Decorative dot grid */}
                <div className="tw-absolute tw-bottom-4 tw-right-4 tw-w-16 tw-h-16 tw-opacity-30 tw-pointer-events-none testi-dot-grid" />
              </div>

              {/* Right: Quote */}
              <div className="lg:tw-col-span-8 tw-p-8 lg:tw-p-12 tw-flex tw-flex-col tw-justify-between">
                <div>
                  {/* Big quote icon */}
                  <div className="tw-mb-6">{QUOTE_ICON}</div>

                  {/* Quote text */}
                  <blockquote
                    className={`tw-text-lg tw-leading-relaxed tw-text-slate-700 tw-font-medium tw-mb-8 tw-transition-all tw-duration-260 ${animDir === "left" ? "testi-slide-out-left" : animDir === "right" ? "testi-slide-out-right" : "testi-slide-in"}`}
                  >
                    "{t.quote}"
                  </blockquote>
                </div>

                {/* Bottom row: dots + arrows */}
                <div className="tw-flex tw-items-center tw-justify-between">
                  {/* Progress dots */}
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => { startAuto(); goTo(idx, idx > active ? "left" : "right"); }}
                        className={`tw-rounded-full tw-transition-all tw-duration-300 tw-border-0 ${active === idx ? "dot-active tw-bg-[#5D44FF]" : "dot-inactive tw-bg-slate-200"}`}
                        aria-label={`Go to testimonial ${idx + 1}`}
                      />
                    ))}
                  </div>

                  {/* Prev / Next */}
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                      onClick={goPrev}
                      className="tw-w-10 tw-h-10 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-[#5D44FF] hover:tw-border-[#5D44FF] tw-text-slate-500 hover:tw-text-white tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 hover:tw-scale-105"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="tw-w-4 tw-h-4" />
                    </button>
                    <button
                      onClick={goNext}
                      className="tw-w-10 tw-h-10 tw-rounded-full tw-border tw-border-solid tw-border-[#5D44FF] tw-bg-[#5D44FF] hover:tw-bg-indigo-600 hover:tw-border-indigo-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 hover:tw-scale-105"
                      aria-label="Next"
                    >
                      <ChevronRight className="tw-w-4 tw-h-4" />
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Mini trust strip ── */}
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-4 sm:tw-gap-8 tw-mt-14">
          {[
            { value: "500+", label: "Happy Hotels" },
            { value: "4.9★", label: "Average Rating" },
            { value: "98%", label: "Satisfaction" },
          ].map((s, i) => (
            <div key={i} className="tw-text-center">
              <p className="tw-text-2xl tw-font-extrabold tw-text-[#5D44FF] tw-mb-0.5">{s.value}</p>
              <p className="tw-text-xs tw-text-slate-400 tw-font-medium">{s.label}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
